import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { useParams } from 'react-router-dom';
import { SubscriptionItem } from 'src/features/subscriptionItems/utils/types';
import { useDeleteSubscriptionItem } from 'src/features/subscriptionItems/api/useDeleteSubscriptionItem';
import { LIST_SUBSCRIPTION_ITEMS_RESOURCE } from 'src/features/subscriptionItems/api/useListSubscriptionItems';

interface DeleteSubscriptionItemModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscriptionItem: SubscriptionItem;
    children?: React.ReactNode;
}

function DeleteSubscriptionItemModal<TData>({
    selectedSubscriptionItem,
    children,
}: DeleteSubscriptionItemModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { subscriptionId = '' } = useParams();

    const { mutate: deleteSubscriptionItem } = useDeleteSubscriptionItem({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(subscriptionItemsMessages.deleteSubscriptionItemSuccess),
                description: formatMessage(subscriptionItemsMessages.deleteSubscriptionItemSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...subscriptionItemsMessages.deleteSubscriptionItem },
                subTitle: {
                    ...subscriptionItemsMessages.deleteSubscriptionItemSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...subscriptionItemsMessages.confirmDeleteSubscriptionItem },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: subscriptionItemsMessages.deleteSubscriptionItem,
                callbackFn: () => {
                    return (
                        deleteSubscriptionItem({
                            itemId: selectedSubscriptionItem.id,
                            subscriptionId,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteSubscriptionItemModal;
